// Ordered, persistent queue for attendance taps. Every tap goes through it
// (even online) so order is preserved and flaky Wi-Fi never loses a tap.
import { api, ApiError, NetworkError } from './api.js';

const DB = 'atnd', STORE = 'queue';
let mem = [];
let flushing = false;
const listeners = new Set();

function idb() {
  return new Promise((res, rej) => {
    const r = indexedDB.open(DB, 1);
    r.onupgradeneeded = () => r.result.createObjectStore(STORE, { keyPath: 'id' });
    r.onsuccess = () => res(r.result);
    r.onerror = () => rej(r.error);
  });
}
async function persist(op, remove = false) {
  try {
    const db = await idb();
    const tx = db.transaction(STORE, 'readwrite');
    remove ? tx.objectStore(STORE).delete(op.id) : tx.objectStore(STORE).put(op);
  } catch { /* private mode: memory only */ }
}

export async function loadQueue() {
  try {
    const db = await idb();
    mem = await new Promise((res, rej) => {
      const r = db.transaction(STORE).objectStore(STORE).getAll();
      r.onsuccess = () => res(r.result.sort((a, b) => a.seq - b.seq));
      r.onerror = () => rej(r.error);
    });
  } catch { mem = []; }
  notify();
}

const notify = () => listeners.forEach((f) => f({ pending: mem.length, flushing }));
export const onQueueChange = (f) => { listeners.add(f); f({ pending: mem.length, flushing }); return () => listeners.delete(f); };
export const pendingCount = () => mem.length;

/** op: {id, method, path, body}. Resolves after enqueue; flush runs in background. */
export function enqueue(op) {
  const full = { ...op, seq: Date.now() * 1000 + (mem.length % 1000) };
  mem.push(full);
  persist(full);
  notify();
  flush();
}

export async function flush() {
  if (flushing) return;
  flushing = true; notify();
  try {
    while (mem.length) {
      const op = mem[0];
      try {
        await api(op.method, op.path, op.body);
      } catch (e) {
        if (e instanceof NetworkError) break; // still offline; retry later
        if (e instanceof ApiError && (e.status >= 500 || e.status === 401)) break;
        // 4xx: the server rejected it (e.g. session already ended); drop it
        console.warn('dropping rejected op', op, e.message);
      }
      mem.shift();
      persist(op, true);
      notify();
    }
  } finally {
    flushing = false; notify();
  }
}

window.addEventListener('online', flush);
setInterval(() => { if (mem.length) flush(); }, 10000);
